class CacheService {
  static MAX_CACHE_SIZE = 100; // Maximum number of cached entries
  static MAX_ENTRY_AGE = 60 * 60 * 1000; // 1 hour - absolute max age for any entry

  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
    this.pendingRequests = new Map();

    // Start automatic cleanup of expired entries
    this.startAutoCleanup();
  }

  getCacheKey(key, params = {}) {
    // Sort object keys for consistent cache keys
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((acc, k) => {
        acc[k] = params[k];
        return acc;
      }, {});

    return `${key}_${JSON.stringify(sortedParams)}`;
  }

  async get(key, fetchFunction, options = {}) {
    const {
      ttl = 5 * 60 * 1000, // 5 minutes default
      forceRefresh = false,
      params = {}
    } = options;

    const cacheKey = this.getCacheKey(key, params);

    // Return cached data if valid
    if (!forceRefresh && this.isValid(cacheKey, ttl)) {
      return this.cache.get(cacheKey);
    }

    // If a request is already in flight, return that promise
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    // Start new request
    const promise = fetchFunction()
      .then(data => {
        this.set(cacheKey, data);
        return data;
      })
      .finally(() => {
        this.pendingRequests.delete(cacheKey);
      });

    this.pendingRequests.set(cacheKey, promise);
    return promise;
  }

  set(key, data) {
    // Enforce max cache size - remove oldest entry if full
    if (this.cache.size >= CacheService.MAX_CACHE_SIZE) {
      this._removeOldestEntry();
    }

    this.cache.set(key, data);
    this.timestamps.set(key, Date.now());
  }

  /**
   * Remove the oldest cache entry based on timestamp
   * @private
   */
  _removeOldestEntry() {
    let oldestKey = null;
    let oldestTime = Infinity;

    for (const [key, timestamp] of this.timestamps.entries()) {
      if (timestamp < oldestTime) {
        oldestTime = timestamp;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
      this.timestamps.delete(oldestKey);
      this.pendingRequests.delete(oldestKey);
    }
  }

  isValid(key, ttl) {
    if (!this.cache.has(key)) return false;
    
    const timestamp = this.timestamps.get(key);
    return Date.now() - timestamp < ttl;
  }

  invalidate(key, params = {}) {
    const cacheKey = this.getCacheKey(key, params);
    this.cache.delete(cacheKey);
    this.timestamps.delete(cacheKey);
    this.pendingRequests.delete(cacheKey);
  }

  invalidatePattern(pattern) {
    // Invalidate all keys matching a pattern
    const keysToDelete = Array.from(this.cache.keys())
      .filter(key => key.startsWith(pattern));
    
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      this.timestamps.delete(key);
      this.pendingRequests.delete(key);
    });
  }

  clear() {
    this.cache.clear();
    this.timestamps.clear();
    this.pendingRequests.clear();
  }

  /**
   * Start automatic cleanup of expired cache entries
   * Runs every 5 minutes to remove entries older than MAX_ENTRY_AGE
   * @private
   */
  startAutoCleanup() {
    // Clean up every 5 minutes
    this.cleanupInterval = setInterval(() => {
      this._cleanupExpiredEntries();
    }, 5 * 60 * 1000);
  }

  /**
   * Remove all entries older than MAX_ENTRY_AGE
   * @private
   */
  _cleanupExpiredEntries() {
    const now = Date.now();
    const keysToDelete = [];

    for (const [key, timestamp] of this.timestamps.entries()) {
      if (now - timestamp > CacheService.MAX_ENTRY_AGE) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => {
      this.cache.delete(key);
      this.timestamps.delete(key);
      this.pendingRequests.delete(key);
    });

    if (keysToDelete.length > 0) {
      console.log(`[CacheService] Cleaned up ${keysToDelete.length} expired entries`);
    }
  }

  /**
   * Stop automatic cleanup (for testing or cleanup)
   */
  stopAutoCleanup() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }

  /**
   * Get cache statistics for monitoring
   * @returns {Object} Cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      maxSize: CacheService.MAX_CACHE_SIZE,
      pendingRequests: this.pendingRequests.size,
      oldestEntry: this._getOldestEntryAge(),
      utilizationPercent: Math.round((this.cache.size / CacheService.MAX_CACHE_SIZE) * 100)
    };
  }

  /**
   * Get age of oldest cache entry in milliseconds
   * @private
   */
  _getOldestEntryAge() {
    if (this.timestamps.size === 0) return 0;

    const now = Date.now();
    let oldestTime = now;

    for (const timestamp of this.timestamps.values()) {
      if (timestamp < oldestTime) {
        oldestTime = timestamp;
      }
    }

    return now - oldestTime;
  }
}

// Create singleton instance and assign to variable
const cacheServiceInstance = new CacheService();

// Export the instance
export default cacheServiceInstance;